// Papier d'apport — COPIE CONFORME du générateur V2
// (src/app/api/generate-apports-pdf/route.js), à ceci près qu'il ne va plus
// chercher ses données : FlashCP les a déjà en mémoire.
//
// La V2 ne fabriquait PAS un PDF. Elle renvoyait ce même HTML avec un
// `window.print()` au chargement, et c'est le navigateur qui imprimait. Rien
// n'exigeait donc un serveur : le document se construit ici, à l'identique —
// A4 paysage, logo, boîte, quatre colonnes de signature, verso de composition.
//
// Ne pas « améliorer » la mise en page : c'est un formulaire d'entreprise, la
// référence visuelle est /data/handoff/apports_20260514.jpg.

import { APPORT_ROULEAUX, APPORT_BILLETS } from 'sotcp-core/inventaireCoffreCalc'
import { escapeHtml as esc } from 'sotcp-core/escapeHtml'
import { LOGO } from './logo'

// Les monnayeurs des caisses automatiques (étape 02A) reprennent aussi des
// billets de 20 €, que le modèle d'apport caissier ne connaît pas — il s'arrête
// à 10 €. Le papier porte donc une colonne de plus, vide pour les caissiers.
//
// C'est aussi la raison pour laquelle le total est recalculé ici plutôt que par
// `sumApport` : celui-ci ignore `b020`, ce qui sous-évaluerait chaque apport SCO
// de tout son 20 €. Pour un apport de caissier, les deux donnent le même chiffre.
const BILLETS = [
  ...APPORT_BILLETS,
  { id: 'b020', denomination: 20, label: 'Billet 20 EUR', sub: '1 billet = 20,00 EUR' },
]

const calcTotal = (d = {}) => {
  let s = 0
  for (const r of APPORT_ROULEAUX) s += (Number(d.rouleaux?.[r.id]) || 0) * r.valeurRouleau
  for (const b of BILLETS)         s += (Number(d.billets?.[b.id])  || 0) * b.denomination
  return s
}

/**
 * Les apports du jour, caissiers PUIS monnayeurs SCO — c'est la même sortie de
 * coffre, le même matin, le même bordereau à signer. Les deux vivent dans des
 * clés différentes du rapport (cf. D21) et ne se rejoignent qu'ici.
 */
function tousLesApports(r) {
  const fusion = { ...(r.apports || {}) }
  const sco = r.apportsSco || {}
  for (const k of Object.keys(sco).sort((a, b) => Number(a) - Number(b))) {
    const m = sco[k] || {}
    fusion[`SCO ${m.caisse ?? k}`] = {
      rouleaux: m.rouleaux || {}, billets: m.billets || {},
      plomb: '', saisie: null, sco: true, monnayeur: m.monnayeur ?? null,
    }
  }
  return fusion
}


function fmt(n) {
  return typeof n === 'number'
    ? n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
    : '—'
}

// Number with 2 decimals, no currency suffix (compact cells / legend).
function fmtNum(n) {
  return (Number(n) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// Short denomination label for matrix column headers: 0.10 → "10 c", 1 → "1 €".
function shortDenom(v) {
  return v < 1 ? `${Math.round(v * 100)} c` : `${v} €`
}

function fmtDateShort(d) {
  const [y, m, day] = d.split('-')
  return `${day}/${m}/${y}`
}

function fmtPublishedDate(d) {
  // "DD/MM/YYYY" of the publication timestamp (render time, or closed_at).
  const dt = d instanceof Date ? d : new Date(d || Date.now())
  return dt.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function fmtPublishedAt(d) {
  // Returns "DD/MM/YYYY à HH:MM:SS" for the bottom-right "Publié le …" line.
  // If we have a closed_at, use it. Otherwise fall back to render time.
  const dt = d instanceof Date ? d : new Date(d || Date.now())
  const time = dt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  return `${fmtPublishedDate(dt)} à ${time}`
}

/**
 * @param {{ date: string, rapport: object, caissiers?: unknown[], clotureeAt?: string|Date|null }} args
 * @returns {string} le document HTML complet, prêt à être imprimé
 */
export function htmlPapierApports({ date, rapport, caissiers: listeCaissiers, clotureeAt }) {
  const r          = rapport || {}
  const apports    = tousLesApports(r)
  const caissiers  = listeCaissiers || []

  const opKeys     = Object.keys(apports)
  const grandTotal = opKeys.reduce((s, k) => s + calcTotal(apports[k]), 0)

  // Publication timestamp — single source so the in-box date and the "Publié le"
  // footer always agree. Use closed_at if available (clôturée), otherwise render
  // time (= today). The document date must reflect when the apport is published,
  // NOT the journée being treated — Kamel 08/06.
  const published   = clotureeAt || new Date()
  const publishedAt = fmtPublishedAt(published)

  // 15 rows per page matches the corporate Excel template (rows 8-22).
  const ROWS_PER_PAGE = 15
  const pageCount     = Math.max(1, Math.ceil(opKeys.length / ROWS_PER_PAGE))

  const renderRow = (key) => {
    const d     = apports[key] || {}
    const total = calcTotal(d)
    const isKO  = d.saisie !== 'OK'
    const op    = caissiers.find(c => [c.prenom, c.nom].filter(Boolean).join(' ') === key)
    // Une machine n'a pas de badge : elle a un numéro de monnayeur. Et « saisi
    // dans ORIKA » n'a pas de sens pour elle — la case reste vide plutôt que de
    // porter un « KO » qui ferait croire à un oubli.
    const badge = d.sco ? (d.monnayeur ?? '—') : (op?.badge || '—')
    const saisieLabel = d.sco ? '—' : (d.saisie === 'OK' ? 'OK' : 'KO')
    return `
      <tr>
        <td class="c-name">${esc(key.toUpperCase())}</td>
        <td class="cell c-mono">${esc(String(badge))}</td>
        <td class="cell c-amt">${fmt(total)}</td>
        <td class="cell c-saisie ${d.sco ? '' : (isKO ? 'is-ko' : 'is-ok')}">${saisieLabel}</td>
        <td class="cell c-mono">${esc(d.plomb || '')}</td>
        <td class="cell c-okko"></td>
        <td class="cell c-sig"></td>
        <td class="cell c-sig"></td>
        <td class="cell c-sig"></td>
        <td class="cell c-sig"></td>
      </tr>`
  }

  const renderEmptyRow = () => `
    <tr class="empty">
      <td class="c-name"></td>
      <td class="cell"></td>
      <td class="cell"></td>
      <td class="cell"></td>
      <td class="cell"></td>
      <td class="cell"></td>
      <td class="cell"></td>
      <td class="cell"></td>
      <td class="cell"></td>
      <td class="cell"></td>
    </tr>`

  // ── Verso: detail-per-apport composition matrix ──────────────────────────
  // One row per operator, columns = each roll/note denomination (quantities),
  // plus the per-operator amount. Compact: blank cells for zero. Column-totals
  // footer on the last detail page. Rendered AFTER the recto signature pages so
  // it lands on the back (verso) when printed double-sided.
  const DETAIL_ROWS_PER_PAGE = 20
  const detailPageCount = opKeys.length > 0 ? Math.ceil(opKeys.length / DETAIL_ROWS_PER_PAGE) : 0

  const colTotals = {}
  for (const r of APPORT_ROULEAUX) colTotals[r.id] = 0
  for (const b of BILLETS)         colTotals[b.id] = 0
  for (const k of opKeys) {
    const d = apports[k] || {}
    for (const r of APPORT_ROULEAUX) colTotals[r.id] += Number(d.rouleaux?.[r.id]) || 0
    for (const b of BILLETS)         colTotals[b.id] += Number(d.billets?.[b.id])  || 0
  }

  const rollLegend = APPORT_ROULEAUX.map(r => `${shortDenom(r.denomination)} = ${fmtNum(r.valeurRouleau)}`).join(' · ')

  const detailHead = `
      <thead>
        <tr>
          <th class="grp" rowspan="2">Opérateur</th>
          <th class="grp" colspan="${APPORT_ROULEAUX.length}">Rouleaux (pièces) — nb</th>
          <th class="grp" colspan="${BILLETS.length}">Billets — nb</th>
          <th class="grp" rowspan="2">Montant</th>
        </tr>
        <tr>
          ${APPORT_ROULEAUX.map(r => `<th class="denom">${shortDenom(r.denomination)}</th>`).join('')}
          ${BILLETS.map(b => `<th class="denom">${shortDenom(b.denomination)}</th>`).join('')}
        </tr>
      </thead>`

  const detailFoot = `
      <tfoot>
        <tr>
          <td class="tfoot-lbl">TOTAL</td>
          ${APPORT_ROULEAUX.map(r => `<td>${colTotals[r.id] || ''}</td>`).join('')}
          ${BILLETS.map(b => `<td>${colTotals[b.id] || ''}</td>`).join('')}
          <td>${fmt(grandTotal)}</td>
        </tr>
      </tfoot>`

  const renderDetailRow = (key) => {
    const d = apports[key] || {}
    const cells = [
      ...APPORT_ROULEAUX.map(r => Number(d.rouleaux?.[r.id]) || 0),
      ...BILLETS.map(b => Number(d.billets?.[b.id]) || 0),
    ].map(q => `<td class="d-qty">${q || ''}</td>`).join('')
    return `<tr><td class="d-name">${esc(key.toUpperCase())}</td>${cells}<td class="d-tot">${fmt(calcTotal(d))}</td></tr>`
  }

  const detailPages = Array.from({ length: detailPageCount }, (_, dIdx) => {
    const start    = dIdx * DETAIL_ROWS_PER_PAGE
    const pageKeys = opKeys.slice(start, start + DETAIL_ROWS_PER_PAGE)
    const isLast   = dIdx === detailPageCount - 1
    return `
  <article class="page">
    <div class="topbar">
      <div class="brand">
        <img src="${LOGO}" alt="Monoprix"/>
        <div class="brand-name">MONOPRIX</div>
      </div>
      <div class="store-id">
        MONOPRIX INTÉGRÉS (1)<br/>
        CROIX ROUSSE (106)
      </div>
    </div>

    <div class="detail-head">
      <div class="detail-title">Détail des apports — composition</div>
      <div class="detail-sub">${esc(fmtPublishedDate(published))}${detailPageCount > 1 ? ` · page ${dIdx + 1}/${detailPageCount}` : ''}</div>
    </div>

    <table class="detail-tbl">
      <colgroup>
        <col style="width:150px;">
        ${APPORT_ROULEAUX.map(() => '<col>').join('')}
        ${BILLETS.map(() => '<col>').join('')}
        <col style="width:92px;">
      </colgroup>
      ${detailHead}
      <tbody>${pageKeys.map(renderDetailRow).join('')}</tbody>
      ${isLast ? detailFoot : ''}
    </table>

    <div class="detail-legend">
      <strong>Valeur d'un rouleau (EUR) :</strong> ${rollLegend} — chaque case indique le <em>nombre</em> de rouleaux / billets.
    </div>

    <div class="pfoot">
      <div class="pfoot-left"></div>
      <div class="pfoot-page">${pageCount + dIdx + 1}</div>
      <div class="pfoot-right">Publié le ${esc(publishedAt)}</div>
    </div>
  </article>`
  }).join('')

  const html = `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8"/>
<title>Apports monnaie — ${fmtDateShort(date)}</title>
<style>
  :root {
    --mono-red:    #dd0033;
    --mono-blue:   #0066cc;
    --zebra-blue:  #eef4fc;
    --ink:         #111;
    --ink-2:       #333;
    --ink-3:       #555;
    --ink-4:       #888;
    --rule:        #000;
    --rule-soft:   #777;
    --grey-fill:   #dadada;
    --font: 'Helvetica Neue', Helvetica, Arial, sans-serif;
    --font-mono: 'Consolas', 'Courier New', monospace;
    --page-w: 1123px;
    --page-h: 794px;
    --page-pad: 32px 44px;
    --name-w: 160px;
  }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: var(--font);
    background: #ddd;
    padding: 24px 0 60px;
    font-size: 11px;
    color: var(--ink);
    line-height: 1.3;
  }

  /* Floating print toolbar */
  .toolbar {
    position: fixed; top: 16px; right: 16px; z-index: 50;
    display: flex; gap: 8px;
    background: var(--ink); color: white;
    padding: 6px; border-radius: 99px;
    box-shadow: 0 6px 24px rgba(0,0,0,0.25);
  }
  .toolbar button {
    background: none; border: none; color: inherit;
    padding: 7px 14px;
    font-family: var(--font-mono); font-size: 11px;
    cursor: pointer; border-radius: 99px; opacity: 0.85;
  }
  .toolbar button:hover { opacity: 1; }
  .toolbar button.primary { background: white; color: var(--ink); opacity: 1; font-weight: 600; }

  .pages { display: flex; flex-direction: column; gap: 24px; align-items: center; }
  .page {
    width: var(--page-w);
    height: var(--page-h);
    background: white;
    padding: var(--page-pad);
    box-shadow: 0 4px 24px rgba(0,0,0,0.08);
    page-break-after: always;
    position: relative;
    display: flex;
    flex-direction: column;
  }
  .page:last-child { page-break-after: auto; }

  /* ── Top bar (outside box) ────────────────────────────────────── */
  .topbar {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 14px;
  }
  .brand {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .brand img {
    width: 34px;
    height: auto;
  }
  .brand-name {
    font-size: 22px;
    font-weight: 700;
    color: var(--mono-red);
    letter-spacing: 0.02em;
  }
  .store-id {
    text-align: right;
    font-size: 11px;
    font-weight: 700;
    color: var(--ink);
    letter-spacing: 0.04em;
    line-height: 1.5;
  }

  /* ── Main table ────────────────────────────────────────────────── */
  .apport-tbl {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    margin: 0 auto;
    page-break-inside: avoid;
  }
  /* Force the thead NOT to repeat on continuation pages. Default browser
     behavior is to clone <thead> on every printed page break, which makes
     a partial overflow look like a duplicated empty form on page 2.       */
  .apport-tbl thead { display: table-row-group; }
  /* Operator-name column: no borders, lives outside the box visually. */
  .c-name {
    width: var(--name-w);
    border: none !important;
    background: white !important;
    padding: 0 14px 0 0;
    text-align: right;
    font-size: 11px;
    font-weight: 700;
    color: var(--ink);
    letter-spacing: 0.02em;
    vertical-align: middle;
  }
  /* All other cells = inside the bordered box. */
  .cell {
    border: 1px solid var(--rule);
    padding: 4px 6px;
    vertical-align: middle;
    text-align: center;
    height: 26px;
    overflow: hidden;
  }

  /* Title row spans all 9 data cols. */
  .title-cell {
    border: 1px solid var(--rule);
    padding: 7px 10px;
    text-align: center;
    font-size: 13px;
    font-weight: 700;
    color: var(--ink);
    background: white;
    letter-spacing: 0.01em;
  }
  /* Info row: 4 inline cells inside the box. */
  .info-cell {
    border: 1px solid var(--rule);
    padding: 5px 8px;
    text-align: center;
    font-size: 10.5px;
    background: white;
    color: var(--ink);
  }

  /* Header rows (2-level). */
  .apport-tbl thead .hdr {
    background: white;
    color: var(--ink);
    font-size: 10.5px;
    font-weight: 700;
    text-align: center;
  }
  .apport-tbl thead .hdr-sub {
    background: white;
    font-size: 10px;
    font-weight: 700;
    color: var(--ink);
    text-align: center;
  }

  /* Body cells. */
  .c-mono   { font-family: var(--font-mono); font-size: 11px; letter-spacing: -0.01em; }
  .c-amt    { font-family: var(--font-mono); font-size: 11px; font-weight: 700; }
  .c-saisie { font-family: var(--font); font-size: 11px; font-weight: 700; }
  .c-saisie.is-ko { color: var(--mono-red); }
  .c-saisie.is-ok { color: #1b8a4a; }
  .c-okko   { /* manual mark on paper */ }
  .c-sig    { /* empty for signature on paper */ }

  /* Zebra striping — only on cells with .cell class (the bordered ones). */
  .apport-tbl tbody tr:nth-child(odd) .cell { background: var(--zebra-blue); }
  .apport-tbl tbody tr:nth-child(even) .cell { background: white; }

  /* Empty filler rows render with no operator name. */
  .apport-tbl tbody tr.empty .c-name { color: transparent; user-select: none; }

  /* TOTAUX row. */
  .totaux-label {
    border: 1px solid var(--rule);
    padding: 7px 10px;
    text-align: center;
    font-size: 12px;
    font-weight: 700;
    color: var(--mono-blue);
    background: white;
    letter-spacing: 0.06em;
  }
  .totaux-amt {
    border: 1px solid var(--rule);
    padding: 7px 10px;
    text-align: center;
    font-family: var(--font-mono);
    font-size: 12px;
    font-weight: 700;
    color: var(--ink);
    background: white;
  }
  .totaux-fill {
    border: 1px solid var(--rule);
    background: var(--grey-fill);
    padding: 7px 10px;
  }

  /* ── Page footer (outside box) ────────────────────────────────── */
  .pfoot {
    margin-top: auto;
    padding-top: 10px;
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: end;
    font-size: 10px;
    color: var(--ink-3);
  }
  .pfoot-left  { text-align: left; }
  .pfoot-page  { text-align: center; font-size: 11px; color: var(--ink); }
  .pfoot-right { text-align: right; }

  /* ── Verso: detail-per-apport matrix ───────────────────────────── */
  .detail-head { margin-bottom: 8px; }
  .detail-title { font-size: 14px; font-weight: 700; color: var(--ink); }
  .detail-sub   { font-size: 10.5px; color: var(--ink-3); margin-top: 1px; }
  .detail-tbl {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    page-break-inside: avoid;
  }
  .detail-tbl th, .detail-tbl td {
    border: 1px solid var(--rule);
    padding: 3px 4px;
    text-align: center;
    font-size: 10px;
    height: 22px;
    overflow: hidden;
  }
  .detail-tbl thead th { background: white; font-weight: 700; }
  .detail-tbl thead .grp { background: var(--grey-fill); font-size: 9.5px; letter-spacing: 0.04em; }
  .detail-tbl thead .denom { font-size: 9.5px; }
  .detail-tbl thead .denom-unit { font-weight: 400; color: var(--ink-4); font-size: 8px; }
  .detail-tbl .d-name { text-align: left; font-weight: 700; font-size: 10px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .detail-tbl .d-qty  { font-family: var(--font-mono); }
  .detail-tbl .d-tot  { font-family: var(--font-mono); font-weight: 700; }
  .detail-tbl tbody tr:nth-child(odd) td { background: var(--zebra-blue); }
  .detail-tbl tbody tr:nth-child(even) td { background: white; }
  .detail-tbl tfoot td { background: white; font-weight: 700; font-family: var(--font-mono); }
  .detail-tbl tfoot .tfoot-lbl { font-family: var(--font); letter-spacing: 0.04em; color: var(--mono-blue); }
  .detail-legend { margin-top: 8px; font-size: 9px; color: var(--ink-3); line-height: 1.5; }

  @media print {
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    body { background: white; padding: 0; }
    .toolbar { display: none !important; }
    .pages { gap: 0; }
    .page {
      box-shadow: none; margin: 0;
      width: 297mm; height: 210mm;
      page-break-after: always;
      padding: 12mm 14mm;
    }
    .page:last-child { page-break-after: auto; }
    .apport-tbl tr { page-break-inside: avoid; }
  }
  @page { size: A4 landscape; margin: 0; }
</style>
</head>
<body>

<div class="toolbar">
  <button class="primary" onclick="window.print()">⎙ Imprimer / PDF</button>
</div>

<div class="pages">
  ${Array.from({ length: pageCount }, (_, pageIdx) => {
    const start    = pageIdx * ROWS_PER_PAGE
    const pageKeys = opKeys.slice(start, start + ROWS_PER_PAGE)
    const fillers  = ROWS_PER_PAGE - pageKeys.length
    const bodyRows = pageKeys.map(renderRow).join('') + Array.from({ length: fillers }, renderEmptyRow).join('')

    return `
  <article class="page">

    <div class="topbar">
      <div class="brand">
        <img src="${LOGO}" alt="Monoprix"/>
        <div class="brand-name">MONOPRIX</div>
      </div>
      <div class="store-id">
        MONOPRIX INTÉGRÉS (1)<br/>
        CROIX ROUSSE (106)
      </div>
    </div>

    <table class="apport-tbl">
      <colgroup>
        <col style="width:${'var(--name-w)'};">  <!-- Opérateur (outside box) -->
        <col style="width:70px;">                  <!-- N° badge -->
        <col style="width:90px;">                  <!-- Montant -->
        <col style="width:90px;">                  <!-- Saisie Orika -->
        <col style="width:75px;">                  <!-- Plomb N° -->
        <col style="width:65px;">                  <!-- Plomb OK/KO -->
        <col>                                       <!-- Signature: Caisse Principale -->
        <col>                                       <!-- Signature: Contrôle CDC -->
        <col>                                       <!-- Signature: Titulaire -->
        <col>                                       <!-- Signature: CDC Remettant -->
      </colgroup>

      <thead>
        <!-- Title row -->
        <tr>
          <td class="c-name"></td>
          <td class="title-cell" colspan="9">Transfert et saisie Apport Monnaie</td>
        </tr>
        <!-- Info row -->
        <tr>
          <td class="c-name"></td>
          <td class="info-cell" colspan="3">Monoprix Croix Rousse</td>
          <td class="info-cell" colspan="2">Contremarque</td>
          <td class="info-cell" colspan="1">0106</td>
          <td class="info-cell" colspan="3">${esc(fmtPublishedDate(published))}</td>
        </tr>
        <!-- Header L1 -->
        <tr>
          <td class="c-name"></td>
          <td class="cell hdr" rowspan="2">N°</td>
          <td class="cell hdr" rowspan="2">Montant</td>
          <td class="cell hdr" rowspan="2">Saisie<br/>Orika</td>
          <td class="cell hdr" colspan="2">Plomb</td>
          <td class="cell hdr" colspan="4">Signatures</td>
        </tr>
        <!-- Header L2 -->
        <tr>
          <td class="c-name"></td>
          <td class="cell hdr-sub">N°</td>
          <td class="cell hdr-sub">OK/KO</td>
          <td class="cell hdr-sub">Caisse<br/>Principale</td>
          <td class="cell hdr-sub">Contrôle<br/>CDC</td>
          <td class="cell hdr-sub">Titulaire</td>
          <td class="cell hdr-sub">CDC<br/>Remettant</td>
        </tr>
      </thead>

      <tbody>
        ${bodyRows}
      </tbody>

      <tfoot>
        <tr>
          <td class="c-name"></td>
          <td class="totaux-label" colspan="2">TOTAUX</td>
          <td class="totaux-amt"  colspan="1">${fmt(grandTotal)}</td>
          <td class="totaux-fill" colspan="6"></td>
        </tr>
      </tfoot>
    </table>

    <div class="pfoot">
      <div class="pfoot-left"></div>
      <div class="pfoot-page">${pageIdx + 1}</div>
      <div class="pfoot-right">Publié le ${esc(publishedAt)}</div>
    </div>

  </article>`
  }).join('')}
  ${detailPages}
</div>

<script>window.onload = () => window.print()</script>
</body>
</html>`


  return html
}
